import { useEffect, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Box,
} from "@mui/material";
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import Loading from '../common/Loading';

export default function AccountStatusCard() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(false);

  const fetchStatus = async () => {
    setError(false);
    setLoading(true);
    try {
      const res = await axiosInstance.get('/account/status');
      setStatus(res.data.data);
    } catch (err) {
      console.error("Error fetching account status:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  if (loading) return <Loading size="sm" />;

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-center">
        <p className="text-red-600 text-sm">Failed to load account balances</p>
        <button
          onClick={fetchStatus}
          className="mt-2 text-sm text-red-700 underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!status) {
    return (
      <Card sx={{ p: 4, textAlign: "center" }}>
        <Typography color="text.secondary">No data available</Typography>
      </Card>
    );
  }

  const { payments, expenses, balance } = status;
  const format = (v) =>
    (v || 0).toLocaleString("en-CA", { style: "currency", currency: "CAD" });

  return (
    <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
      <CardContent>
        <Grid container spacing={2}>
          {/* Cash */}
          <Grid item xs={12} sm={4}>
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "#f0fdf4", textAlign: "center" }}>
              <AttachMoneyIcon fontSize="large" sx={{ mb: 1, color: "#16a34a" }} />
              <Typography variant="subtitle2" color="text.secondary">Cash</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: balance.cash >= 0 ? "#16a34a" : "#dc2626" }}>
                {format(balance.cash)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                In: {format(payments.cash)} | Out: {format(expenses.cash)}
              </Typography>
            </Box>
          </Grid>

          {/* Bank */}
          <Grid item xs={12} sm={4}>
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "#eff6ff", textAlign: "center" }}>
              <AccountBalanceIcon fontSize="large" sx={{ mb: 1, color: "#2563eb" }} />
              <Typography variant="subtitle2" color="text.secondary">Bank</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: balance.bank >= 0 ? "#16a34a" : "#dc2626" }}>
                {format(balance.bank)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                In: {format(payments.bank)} | Out: {format(expenses.bank)}
              </Typography>
            </Box>
          </Grid>

          {/* Card */}
          <Grid item xs={12} sm={4}>
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "#faf5ff", textAlign: "center" }}>
              <CreditCardIcon fontSize="large" sx={{ mb: 1, color: "#7c3aed" }} />
              <Typography variant="subtitle2" color="text.secondary">Card</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: balance.card >= 0 ? "#16a34a" : "#dc2626" }}>
                {format(balance.card)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                In: {format(payments.card)} | Out: {format(expenses.card)}
              </Typography>
            </Box>
          </Grid>

          {/* Net Total */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ textAlign: "center", py: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">Net Total</Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: (balance.cash + balance.bank + balance.card) >= 0 ? "#16a34a" : "#dc2626",
                }}
              >
                {format(balance.cash + balance.bank + balance.card)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
